import { useEffect, useState } from "react";
import type { AdminConnectivityView, AuthSessionView } from "@ubisafe/shared";
import { AppShell, type AppView } from "./components/AppShell";
import { BrandLogo } from "./components/BrandLogo";
import { api, ApiClientError } from "./lib/api";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { ClientsPage } from "./pages/ClientsPage";
import { ConnectivityPage } from "./pages/ConnectivityPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/LoginPage";
import { PreImplementationPage } from "./pages/PreImplementationPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { TenantCatalogPage } from "./pages/TenantCatalogPage";
import { UsersPage } from "./pages/UsersPage";

function useBrowserPath() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const update = () => setPath(window.location.pathname);
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  const navigate = (nextPath: string) => {
    window.history.pushState({}, "", nextPath);
    setPath(nextPath);
  };

  return { path, navigate };
}

function LoadingScreen() {
  return (
    <div className="loading-screen" role="status">
      <BrandLogo />
      <div className="loading-spinner" aria-hidden="true" />
      <span>Carregando ambiente seguro…</span>
    </div>
  );
}

export function App() {
  const { path, navigate } = useBrowserPath();
  const [session, setSession] = useState<AuthSessionView | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [connectivityAttention, setConnectivityAttention] = useState(false);

  useEffect(() => {
    if (path === "/redefinir-senha") {
      setCheckingSession(false);
      return;
    }

    let active = true;
    api
      .getSession()
      .then((current) => {
        if (active) setSession(current);
      })
      .catch((error) => {
        if (active && (!(error instanceof ApiClientError) || error.status !== 401)) {
          console.error("Falha ao consultar sessão", error);
        }
        if (active) setSession(null);
      })
      .finally(() => {
        if (active) setCheckingSession(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const isAdministrator = session?.user.roles.includes("ADMIN") ?? false;
    if (!isAdministrator || session?.user.mustChangePassword) {
      setConnectivityAttention(false);
      return;
    }

    let active = true;
    const inspect = async () => {
      try {
        const status = await api.getConnectivity();
        if (active) setConnectivityAttention(status.vpn.status !== "CONNECTED" || status.mysql.status !== "AVAILABLE");
      } catch {
        if (active) setConnectivityAttention(true);
      }
    };
    void inspect();
    const timer = window.setInterval(() => void inspect(), 30_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [session?.user.id, session?.user.mustChangePassword, session?.user.roles.join("|")]);

  const updateConnectivityAttention = (status: AdminConnectivityView) => {
    setConnectivityAttention(status.vpn.status !== "CONNECTED" || status.mysql.status !== "AVAILABLE");
  };

  if (path === "/redefinir-senha") {
    const token = new URLSearchParams(window.location.search).get("token") ?? "";
    return <ResetPasswordPage token={token} onBack={() => navigate("/login")} />;
  }

  if (checkingSession) return <LoadingScreen />;

  if (!session) {
    if (path === "/esqueci-senha") {
      return <ForgotPasswordPage onBack={() => navigate("/login")} />;
    }
    return (
      <LoginPage
        onAuthenticated={(current) => {
          setSession(current);
          navigate("/");
        }}
        onForgotPassword={() => navigate("/esqueci-senha")}
      />
    );
  }

  if (session.user.mustChangePassword) {
    return <ChangePasswordPage onChanged={(current) => { setSession(current); navigate("/"); }} />;
  }

  const isAdmin = session.user.roles.includes("ADMIN");
  const canViewClients = isAdmin || session.user.roles.includes("FINANCE");
  const canManagePreImplementation = isAdmin || session.user.roles.includes("PRE_IMPLEMENTATION");
  const canViewPreImplementation = canManagePreImplementation || session.user.roles.includes("FINANCE");
  const currentView: AppView =
    path === "/usuarios" && isAdmin
      ? "users"
      : path === "/conectividade" && isAdmin
        ? "connectivity"
        : path === "/classificacao" && isAdmin
        ? "catalog"
        : path === "/pocs" && canViewPreImplementation
          ? "pre-implementation"
          : path === "/clientes" && canViewClients
            ? "clients"
            : "home";

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setSession(null);
      navigate("/login");
    }
  };

  return (
    <AppShell
      session={session}
      currentView={currentView}
      onNavigate={(view) => {
        const paths: Record<AppView, string> = {
          home: "/",
          clients: "/clientes",
          "pre-implementation": "/pocs",
          catalog: "/classificacao",
          connectivity: "/conectividade",
          users: "/usuarios",
        };
        navigate(paths[view]);
      }}
      onLogout={logout}
      connectivityAttention={connectivityAttention}
    >
      {currentView === "users" ? (
        <UsersPage currentUserId={session.user.id} />
      ) : currentView === "connectivity" ? (
        <ConnectivityPage onStatusChange={updateConnectivityAttention} />
      ) : currentView === "catalog" ? (
        <TenantCatalogPage />
      ) : currentView === "pre-implementation" ? (
        <PreImplementationPage readOnly={!canManagePreImplementation} />
      ) : currentView === "clients" ? (
        <ClientsPage />
      ) : (
        <HomePage session={session} />
      )}
    </AppShell>
  );
}
